/**
 * `/inventory` (On hand tab) — the merged blank list (UI refresh 2026-08, replaces the separate
 * "On hand" + "Short & reorder" tabs; docs/inventory-ui-refresh-plan-v1.md §3.2).
 *
 * Overview-driven: `GET /inventory/overview` is the row universe (union of stocked + demanded
 * blanks, so a demanded-but-never-stocked blank is never dropped), with the archived supplement
 * from `GET /inventory?includeArchived` behind the "Show archived" toggle — the overview excludes
 * archived-zero-demand rows, and an archived blank must stay reachable to be unarchived.
 *
 * Rows group into brand+style FAMILIES with sizes in wearing order (groupBlankFamilies — fixes the
 * raw-SKU sort that put 5XL on top of every tee family); filter chips replace the old attention
 * toggle + the whole second tab; needs-action families float first so the overview's worst-first
 * contract survives grouping. Tapping a row's identity opens the shared Manage drawer.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, ChevronDown, Package, Search, Settings2, Shirt } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { BlankDetailDialog } from './blank-detail-dialog';
import { type BlankFamily, blankLabel, groupBlankFamilies, variantLabel } from './inventory-lib';
import { ReorderConfigDialog } from './reorder-config-dialog';

type OverviewRow = components['schemas']['InventoryOverviewRow'];
type BlankOnHand = components['schemas']['BlankOnHand'];

/**
 * Optional S&S colorway images on the blank views. They ship with the backend enrichment branch
 * (feat/blank-image-enrichment) whose types.gen regen carries them; until that merges, the fields
 * are simply absent and every row falls back to the icon tile — so this narrow cast (documented,
 * additive-optional reads only) keeps the two branches independently mergeable.
 */
type WithImages = { imageUrl?: string; swatchUrl?: string };
const imagesOf = (r: OverviewRow): WithImages => r as OverviewRow & WithImages;

/** Seed the shared drawer from an overview row (it needs a BlankOnHand-shaped identity + on-hand). */
function toBlankSeed(r: OverviewRow): BlankOnHand {
  return {
    ssSku: r.ssSku,
    onHand: r.onHand,
    brandName: r.brandName,
    styleName: r.styleName,
    colorName: r.colorName,
    sizeName: r.sizeName,
    lastCountedAt: r.lastCountedAt,
    archived: r.archived,
  };
}

/**
 * An archived blank the overview dropped (archived + zero demand), lifted to the overview row shape
 * so one list renders both sources. All reorder math is zeroed — a retired blank has no demand and
 * takes no action; it renders muted with the single "archived" chip.
 */
function archivedToRow(b: BlankOnHand): OverviewRow {
  return {
    ssSku: b.ssSku,
    brandName: b.brandName,
    styleName: b.styleName,
    colorName: b.colorName,
    sizeName: b.sizeName,
    onHand: b.onHand,
    lastCountedAt: b.lastCountedAt,
    demand: 0,
    shortage: 0,
    manualPar: null,
    suggestedRop: null,
    confidence: 'none',
    effectiveThreshold: null,
    belowThreshold: false,
    suggestedOrderQty: 0,
    onOrder: 0,
    archived: true,
  };
}

type Chip = 'all' | 'attention' | 'short' | 'below' | 'uncounted' | 'negative';

const CHIP_LABEL: Record<Chip, string> = {
  all: 'All',
  attention: 'Needs attention',
  short: 'Short',
  below: 'To reorder',
  uncounted: 'Never counted',
  negative: 'Negative',
};

/**
 * Chip predicates mirror the server KPIs (inventory-overview.ts) exactly:
 * - `short` counts shortage>0 REGARDLESS of archived — an archived blank the queue still demands is
 *   the fulfillment signal the overview deliberately keeps (its KPI counts it; hiding it here made
 *   the hub tile and the chip disagree — review finding, 2026-08-08).
 * - `uncounted`/`negative` exclude archived AND (via the caller's `tracked` predicate for uncounted)
 *   demand-only phantom rows that have no ledger history — a never-stocked blank isn't a bin anyone
 *   can count.
 * - `attention` = short ∪ below ∪ negative. Never-counted deliberately NOT in the union: it has its
 *   own chip, and folding it in made this count disagree with the hub tab badge.
 */
function matchesChip(r: OverviewRow, chip: Chip, tracked: (ssSku: string) => boolean): boolean {
  if (r.archived) {
    // A retired blank stays quiet EXCEPT for open-order shortage, which the server still counts.
    if (chip === 'short' || chip === 'attention') return r.shortage > 0;
    return chip === 'all';
  }
  switch (chip) {
    case 'short':
      return r.shortage > 0;
    case 'below':
      return r.belowThreshold;
    case 'uncounted':
      return r.lastCountedAt == null && tracked(r.ssSku);
    case 'negative':
      return r.onHand < 0;
    case 'attention':
      return r.shortage > 0 || r.belowThreshold || r.onHand < 0;
    default:
      return true;
  }
}

/** Worst-first weight for family ordering — mirrors the overview's needs-action severity. An
 *  archived-but-demanded row keeps its shortage weight (the server treats it as needing action). */
function rowSeverity(r: OverviewRow): number {
  if (r.archived) return r.shortage;
  return Math.max(r.shortage, r.suggestedOrderQty, r.belowThreshold ? 1 : 0, r.onHand < 0 ? 1 : 0);
}

const EMPTY_BY_CHIP: Record<Exclude<Chip, 'all'>, string> = {
  attention: 'Nothing needs attention — every blank covers its open orders and its reorder level.',
  short: 'Nothing is short — open orders are covered.',
  below: 'Nothing needs reordering.',
  uncounted: 'Every blank has been counted.',
  negative: 'No blank is negative.',
};

export function InventoryPage() {
  const client = useApiClient();
  const { state } = useAuth();
  const canEditModel = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [managing, setManaging] = useState<BlankOnHand | null>(null);
  const [modelOpen, setModelOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // The URL is the single source of truth for the active chip — the hub KPI tiles deep-link with
  // ?filter=, and a tile clicked while ALREADY on this tab must still switch the filter (a
  // one-shot useState seed left the tile inert in the most common state — review finding,
  // 2026-08-08). Legacy ?attention= bookmarks (uncounted/negative) still work. Chip clicks
  // navigate, so back/forward walks filter history too.
  const navigate = useNavigate();
  const params = useSearch({ strict: false }) as { filter?: string; attention?: string };
  const fromUrl = params.filter ?? params.attention;
  const chip: Chip = fromUrl && fromUrl in CHIP_LABEL ? (fromUrl as Chip) : 'all';
  const setChip = (next: Chip) =>
    navigate({
      to: '/inventory',
      search: next === 'all' ? {} : { filter: next },
      replace: true,
    });

  const overview = useQuery({
    queryKey: ['inventory-overview'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/overview');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the overview.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Ledger membership — mirrors the server KPIs' `ledger()` predicate so the "Never counted" chip
  // can't count demanded-never-stocked phantom rows (they have no bin to count — review finding,
  // 2026-08-08). Shares the hub's ['inventory'] query key, so under the hub this is a cache read,
  // not a second request. Until it loads, everything counts as tracked (the old behavior) rather
  // than under-counting to zero.
  const ledgerQuery = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inventory.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });
  const ledgerSet = useMemo(
    () => (ledgerQuery.data ? new Set(ledgerQuery.data.map((b) => b.ssSku)) : null),
    [ledgerQuery.data],
  );
  const tracked = (ssSku: string) => ledgerSet === null || ledgerSet.has(ssSku);

  // The archived supplement — only fetched when shown. Keeps its own key (the default ['inventory']
  // cache stays archive-free for the hub's Add-stock dialog).
  const archivedQuery = useQuery({
    queryKey: ['inventory', 'with-archived'],
    enabled: showArchived,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory', {
        params: { query: { includeArchived: 'true' } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inventory.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });

  const rows = useMemo(() => {
    const base = overview.data?.rows ?? [];
    if (!showArchived) return base;
    const seen = new Set(base.map((r) => r.ssSku));
    const supplement = (archivedQuery.data ?? [])
      .filter((b) => b.archived && !seen.has(b.ssSku))
      .map(archivedToRow);
    return [...base, ...supplement];
  }, [overview.data, archivedQuery.data, showArchived]);

  const q = search.trim().toLowerCase();
  const visible = rows.filter(
    (r) =>
      matchesChip(r, chip, tracked) &&
      (!q || `${blankLabel(r)} ${variantLabel(r)} ${r.ssSku}`.toLowerCase().includes(q)),
  );
  const families = useMemo(() => groupBlankFamilies(visible, rowSeverity), [visible]);

  const chipCount = (c: Chip) => rows.filter((r) => matchesChip(r, c, tracked)).length;
  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-4">
      {overview.isError && !overview.data && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load inventory</AlertTitle>
          <AlertDescription>
            {overview.error instanceof Error ? overview.error.message : 'Network error.'}
          </AlertDescription>
        </Alert>
      )}
      {overview.isError && overview.data && (
        <p className="text-sm text-destructive">Couldn't refresh — showing the last loaded list.</p>
      )}

      {overview.isPending && <Skeleton className="h-40 w-full" />}

      {overview.isSuccess && rows.length === 0 && !showArchived && <EmptyState />}

      {overview.isSuccess && (rows.length > 0 || showArchived) && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 basis-52 sm:max-w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search blank, color, SKU…"
                className="h-9 pl-8"
                aria-label="Search blanks"
              />
            </div>
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setShowArchived((v) => !v)}
              >
                <Archive className="size-4" />
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setModelOpen(true)}
                title={canEditModel ? 'Edit the reorder model' : 'View the reorder model'}
              >
                <Settings2 className="size-4" />
                Reorder settings
              </Button>
            </div>
          </div>

          <fieldset className="flex flex-wrap gap-1.5">
            <legend className="sr-only">Filter blanks</legend>
            {(Object.keys(CHIP_LABEL) as Chip[]).map((c) => {
              const n = chipCount(c);
              return (
                <Button
                  key={c}
                  variant={chip === c ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full"
                  onClick={() => setChip(chip === c && c !== 'all' ? 'all' : c)}
                >
                  {CHIP_LABEL[c]}
                  <span
                    className={cn(
                      'tabular-nums',
                      chip === c ? 'opacity-70' : 'text-muted-foreground',
                    )}
                  >
                    {n}
                  </span>
                </Button>
              );
            })}
          </fieldset>

          {/* A silent supplement failure reads as "the archived blanks were deleted" — the operator's
              next move is re-creating one via Restock, minting a duplicate ledger row. Say it. */}
          {showArchived && archivedQuery.isError && (
            <p className="text-sm text-destructive">
              Couldn't load the archived blanks — showing active ones only. Retry with Refresh.
            </p>
          )}
          {showArchived && archivedQuery.isPending && (
            <p className="text-sm text-muted-foreground">Loading archived blanks…</p>
          )}

          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{visible.length}</span> of {rows.length}{' '}
            blanks · {families.length} {families.length === 1 ? 'family' : 'families'}
          </p>

          {visible.length === 0 ? (
            <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
              {q
                ? `Nothing matches “${search.trim()}”.`
                : chip === 'all'
                  ? 'No blanks tracked yet.'
                  : EMPTY_BY_CHIP[chip]}
            </p>
          ) : (
            families.map((g) => (
              <FamilyGroup
                key={g.key}
                group={g}
                collapsed={collapsed.has(g.key)}
                onToggle={() => toggleCollapsed(g.key)}
                onManage={(r) => setManaging(toBlankSeed(r))}
              />
            ))
          )}
        </>
      )}

      <BlankDetailDialog blank={managing} onClose={() => setManaging(null)} />
      <ReorderConfigDialog open={modelOpen} onClose={() => setModelOpen(false)} />
    </div>
  );
}

/* Desktop row grid: identity | on hand | orders need | short | reorder level | to buy | state. */
const ROW_GRID =
  'md:grid md:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_4rem_6.5rem_4.5rem_9.5rem] md:items-center md:gap-x-2';

function FamilyGroup({
  group,
  collapsed,
  onToggle,
  onManage,
}: {
  group: BlankFamily<OverviewRow>;
  collapsed: boolean;
  onToggle: () => void;
  onManage: (row: OverviewRow) => void;
}) {
  const total = group.rows.reduce((sum, r) => sum + r.onHand, 0);
  const shortSum = group.rows.reduce((sum, r) => sum + r.shortage, 0);
  const belowCount = group.rows.filter((r) => r.belowThreshold).length;
  // Negative / never-counted children must survive a COLLAPSED header — without these badges the
  // only discoverable signal was the page-level chip count (review finding, 2026-08-08).
  const negativeCount = group.rows.filter((r) => !r.archived && r.onHand < 0).length;
  const uncountedCount = group.rows.filter((r) => !r.archived && r.lastCountedAt == null).length;
  const headerImage = group.rows.map((r) => imagesOf(r).imageUrl).find(Boolean);

  return (
    <section className="overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-3 bg-muted/50 px-3 py-2 text-left hover:bg-muted/70"
      >
        {headerImage ? (
          <img
            src={headerImage}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-md border bg-background object-cover"
          />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground">
            <Shirt className="size-5" />
          </span>
        )}
        <span className="min-w-0">
          <span className="block truncate font-semibold">
            {group.label}
            {group.color ? ` — ${group.color}` : ''}
          </span>
          <span className="block text-xs text-muted-foreground">
            {group.rows.length}{' '}
            {group.color
              ? group.rows.length === 1
                ? 'size'
                : 'sizes'
              : group.rows.length === 1
                ? 'colorway'
                : 'colorways'}{' '}
            · {total} on hand
          </span>
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {shortSum > 0 && (
            <Badge variant="outline" className={cn('font-normal', RED_BADGE)}>
              {shortSum} short
            </Badge>
          )}
          {belowCount > 0 && (
            <Badge variant="outline" className={cn('font-normal', AMBER_BADGE)}>
              {belowCount} to reorder
            </Badge>
          )}
          {negativeCount > 0 && (
            <Badge variant="outline" className={cn('font-normal', RED_BADGE)}>
              {negativeCount} negative
            </Badge>
          )}
          {uncountedCount > 0 && (
            <Badge variant="outline" className={cn('font-normal', AMBER_BADGE)}>
              {uncountedCount} never counted
            </Badge>
          )}
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              collapsed && '-rotate-90',
            )}
            aria-hidden
          />
        </span>
      </button>

      {!collapsed && (
        <>
          <div
            className={cn(
              ROW_GRID,
              'hidden border-t px-3 py-1.5 text-xs font-medium text-muted-foreground md:grid',
            )}
          >
            <span />
            <span className="text-right">On hand</span>
            <span className="text-right">Orders need</span>
            <span className="text-right">Short</span>
            <span className="text-right">Buy up to</span>
            <span className="text-right">To buy</span>
            <span className="text-right">State</span>
          </div>
          <ul>
            {group.rows.map((r) => (
              <BlankRow
                key={r.ssSku}
                row={r}
                showColor={group.color === null}
                onManage={onManage}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * The small print under the target: "40 level + 31 orders", or "suggested" for a blank running on the
 * computed ROP with no owner-set par. Returns null rather than printing an equation that doesn't add up
 * — the engine floors the target at the bare par/ROP, so on the rare row where that floor wins the two
 * halves don't sum. Also null against an older API, where `bufferTarget` is simply absent.
 */
function targetSubLine(r: OverviewRow): string | null {
  const level = r.bufferTarget;
  if (level != null && r.effectiveThreshold != null && level + r.demand === r.effectiveThreshold) {
    if (r.demand > 0) return `${level} level + ${r.demand} orders`;
  }
  return r.manualPar == null && r.suggestedRop != null ? 'suggested' : null;
}

function BlankRow({
  row: r,
  showColor,
  onManage,
}: {
  row: OverviewRow;
  showColor: boolean;
  onManage: (row: OverviewRow) => void;
}) {
  const negative = r.onHand < 0;
  const archived = r.archived ?? false;
  // Shortage tints the row even when archived — the server still counts it as needing action.
  const needsAction = r.shortage > 0 || (!archived && (r.belowThreshold || negative));
  const size = r.sizeName || '—';
  const rowImage = imagesOf(r).imageUrl;

  return (
    <li
      className={cn(
        ROW_GRID,
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-3 py-2',
        needsAction ? 'bg-red-50/60 dark:bg-red-950/30' : 'odd:bg-muted/20',
        archived && 'opacity-60',
      )}
    >
      <button
        type="button"
        onClick={() => onManage(r)}
        className="flex min-h-9 min-w-0 flex-1 items-center gap-2.5 rounded text-left md:flex-initial"
        title="Manage this blank"
      >
        {showColor ? (
          rowImage ? (
            <img
              src={rowImage}
              alt=""
              loading="lazy"
              className="size-8 shrink-0 rounded-md border bg-background object-cover"
            />
          ) : (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground">
              <Shirt className="size-4" />
            </span>
          )
        ) : (
          <span className="flex h-8 min-w-11 shrink-0 items-center justify-center rounded-md border bg-background px-1.5 text-sm font-bold">
            {size}
          </span>
        )}
        <span className="min-w-0">
          {showColor && (
            <span className="block truncate text-sm font-medium">
              {r.colorName || variantLabel(r) || '—'}
            </span>
          )}
          <span className="block truncate font-mono text-xs text-muted-foreground">
            {showColor ? `${size} · ${r.ssSku}` : r.ssSku}
          </span>
        </span>
      </button>

      {/* Phone: the one number that matters, thumb-side. */}
      <span className="text-right md:hidden">
        <span
          className={cn(
            'block text-base font-semibold tabular-nums',
            negative && 'text-red-700 dark:text-red-400',
          )}
        >
          {r.onHand}
        </span>
        <span className="block text-[10px] text-muted-foreground">on hand</span>
      </span>

      <span
        className={cn(
          'hidden text-right font-semibold tabular-nums md:block',
          negative && 'text-red-700 dark:text-red-400',
        )}
      >
        {r.onHand}
      </span>
      <span className="hidden text-right tabular-nums text-muted-foreground md:block">
        {r.demand > 0 ? r.demand : '—'}
      </span>
      <span
        className={cn(
          'hidden text-right tabular-nums md:block',
          r.shortage > 0 ? 'font-semibold text-red-700 dark:text-red-400' : 'text-muted-foreground',
        )}
      >
        {r.shortage > 0 ? r.shortage : '—'}
      </span>
      {/* The TARGET (buffer + committed orders), with its two halves underneath — otherwise a row
          reading "on hand 40 · buy up to 71" with a par of 40 looks like a bug. Keeps the "suggested"
          marker for a blank running on the computed ROP instead of an owner-set par. */}
      <span className="hidden text-right tabular-nums text-muted-foreground md:block">
        {r.effectiveThreshold ?? r.manualPar ?? r.suggestedRop ?? '—'}
        {targetSubLine(r) && (
          <span className="block text-[10px] leading-tight">{targetSubLine(r)}</span>
        )}
      </span>
      <span
        className={cn(
          'hidden text-right tabular-nums md:block',
          r.suggestedOrderQty > 0
            ? 'font-semibold text-red-700 dark:text-red-400'
            : 'text-muted-foreground',
        )}
      >
        {r.suggestedOrderQty > 0 ? r.suggestedOrderQty : '—'}
        {r.onOrder > 0 && (
          <span className="block text-[10px] font-normal" title="Units already on an open PO">
            {r.onOrder} on order
          </span>
        )}
      </span>

      {/* State chips: right-aligned column on desktop; on phones only non-ok chips wrap below. */}
      <span className="flex basis-full flex-wrap gap-1 pl-[3.375rem] md:basis-auto md:justify-end md:pl-0">
        <StateBadges
          short={r.shortage > 0}
          negative={negative}
          belowLevel={r.belowThreshold}
          neverCounted={r.lastCountedAt == null}
          archived={archived}
        />
      </span>
    </li>
  );
}

const RED_BADGE = 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-400';
const AMBER_BADGE = 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400';
const MUTED_BADGE = 'border-muted-foreground/40 text-muted-foreground';

/** Per-blank state chips — short / negative / below-level (red) + never-counted (amber) + archived
 *  (muted); "ok" (desktop only — noise on a phone card) when none apply. */
function StateBadges({
  short,
  negative,
  belowLevel,
  neverCounted,
  archived,
}: {
  short: boolean;
  negative: boolean;
  belowLevel: boolean;
  neverCounted: boolean;
  archived: boolean;
}) {
  if (archived) {
    // An archived blank stays quiet — EXCEPT for open-order shortage, the one state the server
    // still counts (the fulfillment signal must not vanish behind the archived chip).
    return (
      <>
        {short && (
          <Badge variant="outline" className={cn('font-normal', RED_BADGE)}>
            short
          </Badge>
        )}
        <Badge variant="outline" className={cn('font-normal', MUTED_BADGE)}>
          archived
        </Badge>
      </>
    );
  }
  const chips = [
    short && { key: 'short', label: 'short', cls: RED_BADGE },
    negative && { key: 'neg', label: 'negative', cls: RED_BADGE },
    // AMBER, not RED: since 2026-08-12 this also flags a blank sitting AT its par whose free stock
    // (net of committed orders) is short. That is a purchasing signal, not the same class of problem as
    // `short` (a customer order that cannot be filled) or a negative ledger.
    belowLevel && { key: 'below', label: 'to reorder', cls: AMBER_BADGE },
    neverCounted && { key: 'uncounted', label: 'never counted', cls: AMBER_BADGE },
  ].filter((c): c is { key: string; label: string; cls: string } => c !== false);

  if (chips.length === 0) {
    return (
      <Badge
        variant="outline"
        className="hidden border-emerald-300 font-normal text-emerald-700 md:inline-flex dark:border-emerald-800 dark:text-emerald-400"
      >
        ok
      </Badge>
    );
  }
  return (
    <>
      {chips.map((c) => (
        <Badge key={c.key} variant="outline" className={cn('font-normal', c.cls)}>
          {c.label}
        </Badge>
      ))}
    </>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Archive className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">No blanks tracked yet</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Receive an S&amp;S shipment on the{' '}
        <span className="inline-flex items-center gap-1 font-medium">
          <Package className="size-3.5" /> Receiving
        </span>{' '}
        tab, or use <span className="font-medium">Restock</span> in the header to enter blanks you
        already have.
      </p>
    </div>
  );
}
